import axios, { AxiosInstance } from "axios";
import { CognitoUserPool } from "amazon-cognito-identity-js";
import { LICENSE_API_KEY } from "../env";
import { getApiBaseUrl, getFeatureDeployHeaders } from "./utils";

/** `auth_method` value that routes session refresh at the backend. */
export const BACKEND_AUTH_METHOD = "backend";

/**
 * Whether the session in this browser was established through the backend.
 * Operations on an existing session key off this rather than the feature flag:
 * the flag is a rollout switch that can be turned off under a session that is
 * already running, and such a session still has to be refreshed the way it was
 * created. Only the decision to start a *new* login reads the flag.
 *
 * The marker survives a reload; the ID token (module memory) does not.
 */
export const isBackendSession = () =>
  localStorage.getItem("auth_method") === BACKEND_AUTH_METHOD;

/**
 * The ID token for a backend session, held in memory only: the refresh cookie
 * reissues it on every load, so persisting a copy buys nothing and leaves one
 * more place for script on the page to read it. Legacy Cognito and SSO sessions
 * still use localStorage, the only place their token survives a reload.
 */
let sessionToken: string | undefined;

// Bumped whenever a session ends or a new one begins. A refresh remembers the
// value it started under, and if it has moved by the time the response lands,
// the session that request was minted for is not the current one: reviving it
// would sign back in a user who logged out, or replace a login that just
// succeeded with the previous user's token.
let sessionEpoch = 0;

/** Orphans every in-flight refresh and stops later ones from joining them. */
const invalidateInFlightRefresh = () => {
  sessionEpoch += 1;
  refreshInFlight = undefined;
};

export const setSessionToken = (token: string) => {
  sessionToken = token;
};

export const clearSessionToken = () => {
  sessionToken = undefined;
  invalidateInFlightRefresh();
};

export const getSessionToken = () => {
  // Backend sessions hold the ID token in memory only. A leftover pre-migration
  // Cognito token in storage must never stand in for it: on reload memory is
  // empty until cookie restore refills it, and returning the stale token would
  // run API calls as the previous session.
  if (isBackendSession()) return sessionToken ?? null;
  return sessionToken ?? localStorage.getItem("token");
};

/**
 * Drops the pre-migration Cognito state that backend sessions never use, so it
 * can't linger in localStorage: the SDK's own keys (only `signOut` clears them)
 * and our copies of its tokens. Local only — `signOut`, not `globalSignOut`,
 * which would end the user's sessions on other devices.
 */
export const clearLegacyCognitoSession = (tenantProfile?: {
  user_pool_id?: string;
  user_pool_client_id?: string;
}) => {
  // Best-effort: callers run this after the backend session is already gone, so
  // a throw (e.g. the profile was swapped for one without pool ids, which makes
  // CognitoUserPool throw) must not stop the storage cleanup below or surface
  // as a failed logout.
  try {
    if (tenantProfile?.user_pool_id && tenantProfile?.user_pool_client_id) {
      new CognitoUserPool({
        UserPoolId: tenantProfile.user_pool_id,
        ClientId: tenantProfile.user_pool_client_id,
      })
        .getCurrentUser()
        ?.signOut();
    }
  } catch {
    // Fall through to the localStorage cleanup.
  }
  ["token", "id_token", "access_token", "refreshToken", "user"].forEach((key) =>
    localStorage.removeItem(key)
  );
};

/**
 * A hint that this browser probably has a session, for optimistic UI only
 * (e.g. start in a loading state instead of flashing empty). A backend session
 * counts before its token arrives, so this stays true after a failed restore.
 *
 * NEVER use it to decide access. That is `resolveAuthGate` in `authGate.ts`.
 */
export const hasSession = () => !!getSessionToken() || isBackendSession();

/** The Cognito challenges the backend login endpoint can hand back. */
export const CHALLENGE = {
  SOFTWARE_TOKEN_MFA: "SOFTWARE_TOKEN_MFA",
  NEW_PASSWORD_REQUIRED: "NEW_PASSWORD_REQUIRED",
} as const;

/**
 * One response shape for both legs of the flow: tokens when authentication
 * completed, or a challenge plus the session token that answers it. The
 * refresh token is never here — the backend keeps it in an HttpOnly cookie.
 */
export type AuthResponse = {
  accessToken?: string;
  idToken?: string;
  challengeName?: string;
  session?: string;
};

/**
 * Auth calls deliberately bypass `apiClient`: its response interceptor reads
 * every 401 as an expired session and answers with a storage wipe and a
 * redirect to /login, which would swallow the 401 a mistyped password is
 * supposed to produce. `withCredentials` carries the refresh cookie.
 *
 * Built on first use, not at module load, since the base URL is derived from
 * `window.location`.
 */
let client: AxiosInstance | undefined;

const authClient = () => {
  if (!client) {
    client = axios.create({
      baseURL: getApiBaseUrl(),
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        // These routes carry no bearer token, so the gateway key is what gets
        // them through — same as the pre-login endpoints.
        "x-api-key": LICENSE_API_KEY,
        ...getFeatureDeployHeaders(),
      },
    });
  }
  return client;
};

const post = async (path: string, body?: unknown): Promise<AuthResponse> => {
  const response = await authClient().post(`/user-management/auth/${path}`, body);
  return response.data ?? {};
};

// The 401 interceptor and the background session refresh can both call refresh
// at once (sleep/wake fires the timer and the first request 401s together).
// The backend rotates the refresh cookie, so a second in-flight POST presents
// a cookie that was just consumed and fails. Sharing one request means every
// waiter gets the same ID token, and the caller that publishes the session
// still does.
let refreshInFlight: Promise<AuthResponse> | undefined;

// Set while logout's revoke is in flight so no new refresh mints against the
// cookie being revoked. A login that comes back with tokens reopens it.
let sessionRevoked = false;

export class SessionRevokedError extends Error {
  constructor() {
    super("Session was revoked by logout");
    this.name = "SessionRevokedError";
  }
}

/**
 * The refresh cookie is shared by every tab, but each tab holds its own token
 * and cache. When another tab signs in as a different user the marker stays
 * `backend`, so no storage event fires; this tab only finds out when its next
 * refresh comes back as someone else.
 */
export class SessionUserChangedError extends Error {
  constructor() {
    super("The signed-in user changed in another tab");
    this.name = "SessionUserChangedError";
  }
}

/** The `sub` of a JWT, or undefined when it can't be read. */
const subjectOf = (jwt?: string) => {
  if (!jwt) return undefined;
  try {
    return decodeJwtPayload(jwt)?.sub as string | undefined;
  } catch {
    return undefined;
  }
};

/** Test-only: clears the module latch between tests. */
export const __resetAuthStateForTests = () => {
  refreshInFlight = undefined;
  sessionRevoked = false;
  sessionToken = undefined;
  sessionEpoch = 0;
};

export const authApi = {
  login: async (username: string, password: string) => {
    const response = await post("login", { username, password });
    if (response.idToken) {
      sessionRevoked = false;
    }
    return response;
  },

  respondToChallenge: async (
    challengeName: string,
    session: string,
    challengeResponses: Record<string, string>
  ) => {
    const response = await post("login", { challengeName, session, challengeResponses });
    if (response.idToken) {
      sessionRevoked = false;
    }
    return response;
  },

  /**
   * Exchanges a SAML tenant's Cognito authorization code for tokens, so the
   * browser never calls Cognito's /oauth2/token itself. Lands the same refresh
   * cookie a native login does, which is why it reopens refresh too.
   */
  ssoCallback: async (code: string, redirectUri: string) => {
    const response = await post("sso/callback", { code, redirectUri });
    if (response.idToken) {
      sessionRevoked = false;
    }
    return response;
  },

  /** Mints a fresh ID token from the refresh cookie. */
  refresh: () => {
    if (sessionRevoked) {
      return Promise.reject(new SessionRevokedError());
    }
    if (!refreshInFlight) {
      const startedUnder = sessionEpoch;
      const request: Promise<AuthResponse> = post("refresh")
        .then((response) => {
          // The session was torn down while this was in flight (logout from
          // another tab, redirectToLogin). The token belongs to a session that
          // no longer exists, so no caller may store or publish it.
          if (startedUnder !== sessionEpoch) {
            throw new SessionRevokedError();
          }
          return response;
        })
        .finally(() => {
          // Identity check: a teardown may have replaced this with a newer request.
          if (refreshInFlight === request) {
            refreshInFlight = undefined;
          }
        });
      refreshInFlight = request;
    }
    return refreshInFlight;
  },

  /**
   * Revokes the refresh token server-side and expires the cookie. Idempotent:
   * a missing or already-revoked cookie still answers 200, since "no session"
   * is the intended end state either way.
   */
  logout: async (): Promise<{ message: string }> => {
    sessionRevoked = true;
    // Waits on in-flight refresh: each refresh rotates the cookie.
    await refreshInFlight?.catch(() => {});
    try {
      return (await post("logout")) as unknown as { message: string };
    } catch (error) {
      sessionRevoked = false;
      throw error;
    }
  },
};

/**
 * Segments are base64url, where `-` and `_` stand in for `+` and `/`, both of
 * which atob rejects. atob then yields one character per byte, so a multi-byte
 * claim has to be read back as UTF-8 rather than Latin-1, which mangles it.
 */
export const decodeJwtPayload = (jwt: string) => {
  const base64 = jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
};

/**
 * Rebuilds the slice of `CognitoUserSession` the app actually reads —
 * `idToken.jwtToken` and `idToken.payload` — from a backend-issued ID token, so
 * Main's login gate, `useSessionRefreshTimer` and `user-context` keep working
 * unchanged. There is no `refreshToken` field: it lives in the cookie, and
 * every consumer already reads that one optionally.
 */
export const toUserSession = (idToken: string) => ({
  idToken: { jwtToken: idToken, payload: decodeJwtPayload(idToken) },
});

/**
 * The one way a fresh login (native, MFA, forced password change, SSO callback)
 * becomes a backend session. Returns the session for the caller to publish.
 *
 * The order is the contract: the ID token is decoded first, so a malformed one
 * throws before anything is written and never leaves the marker set with no
 * session behind it. The marker then lands before the token and before the
 * caller publishes, because anything that reacts to the published session has
 * to find it already set or it renews down the Cognito path.
 *
 * It also orphans any refresh still in flight: a cookie restore that began on
 * the login page belongs to the previous user and must not land after this
 * login and replace it.
 */
export const establishBackendSession = (
  idToken: string,
  tenantProfile?: { user_pool_id?: string; user_pool_client_id?: string }
) => {
  const session = toUserSession(idToken);
  localStorage.setItem("auth_method", BACKEND_AUTH_METHOD);
  clearLegacyCognitoSession(tenantProfile);
  invalidateInFlightRefresh();
  setSessionToken(idToken);
  return session;
};

/**
 * Mints a fresh ID token and holds it, leaving callers to decide what to
 * publish. Restore, the background timer and the 401 interceptor all land here.
 */
export const refreshIdToken = async () => {
  const { idToken } = await authApi.refresh();
  if (!idToken) {
    throw new Error("Backend refresh returned no ID token");
  }
  // Another tab's logout removes the marker before this tab's storage event is
  // delivered, so the epoch can still be unchanged here. The marker is what
  // says whether a backend session exists to refresh.
  if (!isBackendSession()) {
    throw new SessionRevokedError();
  }
  // Same cookie, different person: another tab logged in as someone else. This
  // tab's cache and UI still belong to the previous user, so the new token must
  // not be swapped in underneath them. Unreadable tokens are not compared.
  const heldSubject = subjectOf(sessionToken);
  const mintedSubject = subjectOf(idToken);
  if (heldSubject && mintedSubject && heldSubject !== mintedSubject) {
    throw new SessionUserChangedError();
  }
  setSessionToken(idToken);
  return idToken;
};

/**
 * The backend collapses every rejected credential — wrong password, bad TOTP
 * code, a new password that fails policy, an expired challenge session — into
 * one flat 401, so only the caller knows what a rejection means where it sits.
 */
export const authErrorMessage = (error: any, rejected = "Invalid email or password") =>
  error?.response?.status === 401 ? rejected : "Something went wrong";
